package com.milta.core.auth

import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Minimal pure-KMP JWT payload decoder.
 *
 * Does NOT verify the signature — the server validates the token on every
 * authenticated request. This parser is used client-side only to read
 * non-sensitive claims (e.g. role) for navigation decisions.
 */
object JwtParser {

    /**
     * Returns the value of the `role` claim from the JWT access token.
     *
     * The ASP.NET Core backend encodes the role under two possible claim names:
     * - Short name: `"role"`
     * - Full ClaimType URI: `"http://schemas.microsoft.com/ws/2008/06/identity/claims/role"`
     *
     * When a PatronChauffeur account carries both `Enterprise` and `Driver` roles,
     * the JWT payload contains a JSON array for the claim. In that case `Enterprise`
     * is returned (Enterprise takes priority over Driver per business rule §5 of MIL-15).
     *
     * @return `"Enterprise"`, `"Driver"`, or `null` when the claim is absent or the
     *         token is malformed.
     */
    fun extractRole(accessToken: String): String? {
        return try {
            val payload = decodePayload(accessToken) ?: return null
            // Try short claim name first, then full URI
            extractRoleFromJson(payload, "role")
                ?: extractRoleFromJson(
                    payload,
                    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
                )
        } catch (_: Exception) {
            null
        }
    }

    /** Account id: the `sub` claim, or `null` when absent / token malformed. */
    fun extractSubject(accessToken: String): String? = extractStringClaim(accessToken, "sub")

    /** Tenant id: the `tenant_id` claim, or `null` when absent / token malformed. */
    fun extractTenantId(accessToken: String): String? = extractStringClaim(accessToken, "tenant_id")

    private fun extractStringClaim(accessToken: String, claimName: String): String? = try {
        decodePayload(accessToken)?.let { extractRoleFromJson(it, claimName) }
    } catch (_: Exception) {
        null
    }

    @OptIn(ExperimentalEncodingApi::class)
    private fun decodePayload(token: String): String? {
        val parts = token.split(".")
        if (parts.size < 2) return null
        val padded = parts[1].padBase64()
        val bytes = Base64.decode(padded)
        return bytes.decodeToString()
    }

    /**
     * Minimal JSON value extractor — avoids pulling in a full JSON library
     * at this layer. Handles string values and JSON arrays.
     *
     * For a string claim: `"role":"Enterprise"` → `"Enterprise"`
     * For an array claim: `"role":["Driver","Enterprise"]` → `"Enterprise"` (first Enterprise wins)
     */
    private fun extractRoleFromJson(json: String, claimName: String): String? {
        // Try to find the claim value — handles both string and array forms
        val keyPattern = "\"$claimName\""
        val keyIndex = json.indexOf(keyPattern)
        if (keyIndex < 0) return null

        val afterColon = json.indexOf(':', keyIndex + keyPattern.length)
        if (afterColon < 0) return null

        // Skip whitespace after ':'
        var pos = afterColon + 1
        while (pos < json.length && (json[pos] == ' ' || json[pos] == '\t')) pos++
        if (pos >= json.length) return null

        return when (json[pos]) {
            '"' -> {
                // Simple string value
                val end = json.indexOf('"', pos + 1)
                if (end < 0) null else json.substring(pos + 1, end)
            }
            '[' -> {
                // Array value — collect all string elements
                val arrayEnd = json.indexOf(']', pos)
                if (arrayEnd < 0) return null
                val arrayContent = json.substring(pos + 1, arrayEnd)
                val roles = arrayContent
                    .split(",")
                    .map { it.trim().removeSurrounding("\"") }
                // Enterprise has priority over Driver (PatronChauffeur rule)
                roles.firstOrNull { it == "Enterprise" }
                    ?: roles.firstOrNull { it == "Driver" }
                    ?: roles.firstOrNull()
            }
            else -> null
        }
    }

    private fun String.padBase64(): String {
        val remainder = length % 4
        return if (remainder == 0) this else this + "=".repeat(4 - remainder)
    }
}
